import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./customers.css";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import {
  Box,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import { FaX, FaUserPlus, FaUserPen, FaCheck } from "react-icons/fa6";
import { toast } from "react-toastify";
import { DataTable } from "../../components/DataTable";
import CustomerForm from "./CustomerForm";
import { useDrawer } from "../../components/DrawerContext.jsx";
import axios from "axios";
import config from "../../config/config.js";
import { useAuth } from '../login/AuthContext';
import { AddCircleOutline, FileDownload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import IconActionsRenderer from "../../components/IconActionsRenderer.jsx";
import { StatusCellRenderer } from "../../components/DataTable/StatusCellRenderer.jsx";

export const Customers = () => {
  const { token, userData, hasPermission } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("aktif");
  const [paketId, setPaketId] = useState("");
  const [programId, setProgramId] = useState("");
  const [paketler, setPaketler] = useState([]);
  const [moduller, setModuller] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDrawerOpen, toggleDrawer, editingCustomer, openDrawerWithCustomer, openDrawerForAddCustomer } = useDrawer();
  const searchTimeoutRef = React.useRef(null);
  const dataFetchTimeoutRef = React.useRef(null);
  const dataTableRef = React.useRef(null);
  const isInitialMount = React.useRef(true);
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
const [restoreCustomerId, setRestoreCustomerId] = useState(null);

  const queryParams = useMemo(
    () => ({
      status: filterStatus,
      ...(paketId ? { paketId } : {}),
      ...(programId ? { programId } : {}),
    }),
    [filterStatus, paketId, programId],
  );

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${config.baseURL}/paketler/paket-detay-list`, { headers })
      .then((r) => setPaketler((r.data || []).filter((p) => p.Etkin !== false && p.Etkin !== 0)))
      .catch(() => {});
    axios
      .get(`${config.baseURL}/program?all=1`, { headers })
      .then((r) => setModuller(r.data || []))
      .catch(() => {});
  }, [token]);

  const handleExportExcel = useCallback(async () => {
    if (!token) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ status: filterStatus });
      if (paketId) params.set("paketId", paketId);
      if (programId) params.set("programId", programId);
      const res = await axios.get(`${config.baseURL}/musteriler/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const type = String(res.headers["content-type"] || "");
      if (type.includes("json") || type.includes("text/plain") || type.includes("text/html")) {
        const text = await res.data.text();
        throw new Error(text || "Excel oluşturulamadı");
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "musteriler.xls";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const blob = e?.response?.data;
      let detail = "";
      try {
        if (blob instanceof Blob) detail = await blob.text();
      } catch {
        detail = "";
      }
      toast.error(detail || e?.message || "Excel indirilemedi");
    } finally {
      setExporting(false);
    }
  }, [token, filterStatus, paketId, programId]);

  // Arama işlemi için debounce uygulanmış fonksiyon
  const handleSearch = useCallback((value) => {
    setSearchText(value);

    // Önceki zamanlayıcıyı temizle
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 3 karakterden az arama yaparken loading gösterme
    setIsLoading(value.length >= 3);
  }, []);

  // Status değişikliği için işleyici
  const handleStatusChange = useCallback((event) => {
    const newStatus = event.target.checked ? "aktif" : "pasif";
    setIsLoading(true);
    setFilterStatus(newStatus);
    setSearchText(""); // Arama kutusunu temizle
  }, []);

  // Veri yükleme için tek bir useEffect - hem queryParams hem searchText değişikliklerini izler
  useEffect(() => {
    // İlk render'da çalışmasını engelle (isInitialMount true ise atla)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Önceki veri yükleme zamanlayıcısını temizle
    if (dataFetchTimeoutRef.current) {
      clearTimeout(dataFetchTimeoutRef.current);
    }

    // Yeni bir zamanlayıcı oluştur - gereksiz istekleri önlemek için
    dataFetchTimeoutRef.current = setTimeout(() => {
      // console.log("Veri yükleniyor - Tetikleyici:", {
      //   filterStatus,
      //   searchText,
      //   searchLength: searchText?.length || 0
      // });

      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);

    // Temizlik fonksiyonu
    return () => {
      if (dataFetchTimeoutRef.current) {
        clearTimeout(dataFetchTimeoutRef.current);
      }
    };
  }, [queryParams, searchText]);

  // Component unmount olduğunda zamanlayıcıları temizle
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (dataFetchTimeoutRef.current) {
        clearTimeout(dataFetchTimeoutRef.current);
      }
    };
  }, []);

  // Müşteri oluşturulduğunda tabloyu yenile ve drawer'ı kapat
  const handleCustomerCreated = useCallback(() => {
    if (dataTableRef.current?.refreshData) {
      dataTableRef.current.refreshData();
    }
    toggleDrawer();
  }, [toggleDrawer]);

  // API İstekleri için axios instance oluşturma
  const axiosInstance = useCallback(() => {
    return axios.create({
      baseURL: config.baseURL,
      headers: { Authorization: `Bearer ${token}` }
    });
  }, [token]);

  // CRUD İşlemleri
  const handleEditCustomer = useCallback(async (customerId) => {
    if (!userData || !token) {
      toast.error("Yetkilendirme hatası");
      return;
    }

    try {
      const api = axiosInstance();
      const response = await api.get(`/musteriler/${customerId}`);
      openDrawerWithCustomer(response.data);
    } catch (error) {
      console.error("Müşteri detay hatası:", error);
      toast.error("Müşteri detayları alınamadı");
    }
  }, [userData, token, openDrawerWithCustomer, axiosInstance]);

  const handleDeleteCustomer = useCallback(async (customerId) => {
    try {
      const api = axiosInstance();
      await api.delete(`/musteriler/${customerId}`);
      toast.success("Müşteri başarıyla silindi");

      // Silme işlemi sonrası dataTable'ı yeniliyoruz
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }

      return true;
    } catch (error) {
      console.error("Silme hatası:", error);
      toast.error("Müşteri silinemedi");
      throw error;
    }
  }, [axiosInstance]);

  const handleRestoreCustomer = useCallback(async (customerId) => {
    try {
      const api = axiosInstance();
      await api.put(`/musteriler/restore/${customerId}`);
      // toast.success("Müşteri başarıyla geri yüklendi");

      // Geri yükleme işlemi sonrası dataTable'ı yeniliyoruz
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }

      return true;
    } catch (error) {
      console.error("Geri yükleme hatası:", error);
      toast.error("Müşteri geri yüklenemedi");
      throw error;
    }
  }, [axiosInstance]);

  const handleViewCustomer = useCallback((customerId) => {
    navigate(`/musteriler/${customerId}`);
  }, [navigate]);

  const handleOpenDeleteModal = useCallback((customerId) => {
    setDeleteCustomerId(customerId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteCustomerId(null);
  }, []);

  const handleOpenRestoreModal = useCallback((customerId) => {
    setRestoreCustomerId(customerId);
    setIsRestoreModalOpen(true);
  }, []);

  const handleCloseRestoreModal = useCallback(() => {
    setIsRestoreModalOpen(false);
    setRestoreCustomerId(null);
  }, []);

  // Müşteriler için özel sütun tanımları
  const customerColumnDefs = useMemo(() => [
    {
      headerName: 'İşlemler',
      field: 'actions',
      maxWidth: 130, // Sabit genişlik, actions sütunu sabit kalmalı
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: IconActionsRenderer,
      pinned: 'left',
      cellClass: 'action-cell-center'
    },
    {
      headerName: 'Bayi',
      field: 'Bayi.BayiAdi',
      valueGetter: params => params.data?.Bayi?.BayiAdi || '',
      sortable: true,
      filter: true,
      minWidth: 130,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Müşteri Kodu',
      field: 'MusteriNo',
      sortable: true,
      filter: true,
      minWidth: 130,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
      }
    },
    {
      headerName: 'Firma Adı',
      field: 'Unvan',
      sortable: true,
      filter: true,
      minWidth: 300,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
      }
    },
    {
      headerName: 'Yetkili',
      field: 'Yetkili',
      sortable: true,
      filter: true,
      minWidth: 200,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Paket',
      field: 'Paket.Adi',
      valueGetter: params => params.data?.PaketListesi || params.data?.Paket?.[0]?.Adi || '',
      sortable: true,
      filter: true,
      minWidth: 100,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Modüller',
      field: 'ModullerText',
      valueGetter: (params) => params.data?.ModullerText || '—',
      sortable: true,
      filter: true,
      minWidth: 220,
      tooltipField: 'ModullerText',
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Email',
      field: 'EPosta',
      sortable: true,
      filter: true,
      minWidth: 200,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Açıklama',
      field: 'Aciklama',
      sortable: true,
      filter: true,
      minWidth: 180,
      valueGetter: (params) => params.data?.Aciklama || "",
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    {
      headerName: 'Telefon',
      field: 'Tel',
      sortable: true,
      filter: true,
      minWidth: 100,
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    },
    // {
    //   headerName: 'Adres',
    //   field: 'Adres',
    //   sortable: true,
    //   filter: true,
    //   minWidth: 250,
    //   filterParams: {
    //     buttons: ['sıfırla', 'uygula'],
    //     closeOnApply: true
    //   }
    // },
    {
          headerName: 'Lisans Durumu',
          field: 'LisansDurumu',
          sortable: true,
          filter: true,
          width: 170,
          cellRenderer: StatusCellRenderer,
          filterParams: {
            buttons: ['sıfırla', 'uygula'],
            closeOnApply: true
          }
        },
    {
      headerName: 'Programlar',
      field: 'LisansProgramlariText',
      sortable: true,
      filter: true,
      minWidth: 160,
      valueGetter: (params) => params.data?.LisansProgramlariText || '—',
      cellRenderer: (params) => {
        const programs = params.data?.LisansProgramlari || [];
        if (!programs.length) return '—';
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {programs.map((p) => {
              const label = p === 'Gastora' ? 'Gastro' : p;
              return (
              <Box
                key={p}
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: label === 'Gastro' ? '#FEF3C7' : '#E8EAF6',
                  color: label === 'Gastro' ? '#92400E' : '#1e293b',
                }}
              >
                {label}
              </Box>
            );
            })}
          </Box>
        );
      },
      filterParams: {
        buttons: ['sıfırla', 'uygula'],
        closeOnApply: true
      }
    }

  ], []);

  // DataTable'a geçirilecek özel context değerleri
  const customContext = useMemo(() => ({
    showRestore: filterStatus !== "aktif",
    showView: filterStatus === "aktif",
    showEdit: filterStatus === "aktif",
    showDelete: filterStatus === "aktif",
    onView: handleViewCustomer,
    onRestore: handleOpenRestoreModal, // <-- Geri yükleme için modalı açmaya değiştirdik
    handleEditOrder: handleEditCustomer, // Geriye dönük uyumluluk için
    handleEditItem: handleEditCustomer,
    handleOpenDeleteModal: handleOpenDeleteModal,
    hasPermission
  }), [filterStatus, handleViewCustomer, handleOpenRestoreModal, handleEditCustomer, handleOpenDeleteModal, hasPermission]);

  if (!userData || !token) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Cari Listesi</h1>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama (en az 3 karakter)"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />

        <FormControl component="fieldset" style={{ marginLeft: '10px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filterStatus === "aktif"}
                onChange={handleStatusChange}
                color="primary"
              />
            }
            label={filterStatus === "aktif" ? "Aktif Müşteriler" : "Pasif Müşteriler"}
          />
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180, ml: 1 }}>
          <InputLabel>Paket</InputLabel>
          <Select
            label="Paket"
            value={paketId}
            onChange={(e) => setPaketId(e.target.value)}
          >
            <MenuItem value="">Tüm paketler</MenuItem>
            {paketler.map((p) => (
              <MenuItem key={p.PaketId || p.Id} value={String(p.PaketId || p.Id)}>
                {p.PaketAdi || p.Adi}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200, ml: 1 }}>
          <InputLabel>Modül</InputLabel>
          <Select
            label="Modül"
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
          >
            <MenuItem value="">Tüm modüller</MenuItem>
            {moduller.map((m) => (
              <MenuItem key={m.Id} value={String(m.Id)}>
                {m.ProgramAdi}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExportExcel}
          disabled={exporting}
          sx={{ ml: 1 }}
          size="medium"
        >
          {exporting ? "Hazırlanıyor..." : "Excel"}
        </Button>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={openDrawerForAddCustomer}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="custom"
        columnDefs={customerColumnDefs}
        apiEndpoint="/musteriler"
        searchEndpoint="/musteriler/search"
        onEditAction={handleEditCustomer}
        onDeleteAction={handleDeleteCustomer}
        onRowDoubleClicked={(e) => e.data?.Id != null && handleViewCustomer(e.data.Id)}
        deleteConfirmMessage="Bu müşteriyi silmek istediğinizden emin misiniz?"
        onLoadingChange={setIsLoading}
        customContext={customContext}
        customQueryParams={queryParams}
        idField="Id"
        pageSize={14}
      />

      <Drawer
        open={isDrawerOpen}
        onClose={toggleDrawer}
        direction="right"
        className="custom-drawer"
        duration={120}
        zIndex={600}
        overlayOpacity={0.25}
      >
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                {editingCustomer ? (
                  <FaUserPen style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                ) : (
                  <FaUserPlus style={{ fontSize: "20px", color: "rgba(0,0,0,0.54)" }} />
                )}
                {editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri Ekle"}
              </Typography>
              <Box display="flex" gap={0.5}>
                <Button
                  type="submit"
                  form="musterikaydetform"
                  variant="text"
                  size="medium"
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaCheck style={{ fontSize: "18px" }} />
                </Button>
                <Button
                  variant="text"
                  size="medium"
                  onClick={toggleDrawer}
                  sx={{
                    minWidth: 'auto',
                    p: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary'
                    }
                  }}
                >
                  <FaX style={{ fontSize: "18px" }} />
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, p: 2.5, pt: 2 }}>
            <CustomerForm
              onCustomerCreated={handleCustomerCreated}
              editingCustomer={editingCustomer}
              fetchCustomers={() => {
                if (dataTableRef.current?.refreshData) {
                  dataTableRef.current.refreshData();
                }
              }}
            />
          </Box>
        </Box>
      </Drawer>
      <Dialog
  open={isDeleteModalOpen}
  onClose={handleCloseDeleteModal}
  aria-labelledby="alert-dialog-title"
  aria-describedby="alert-dialog-description"
>

</Dialog>
{/* Silme Modalı */}
<Dialog
  open={isDeleteModalOpen}
  onClose={handleCloseDeleteModal}
  aria-labelledby="delete-dialog-title"
  aria-describedby="delete-dialog-description"
>
  <DialogTitle id="delete-dialog-title">
    {"Silme İşlemi"}
  </DialogTitle>
  <DialogContent>
    <DialogContentText id="delete-dialog-description">
      Bu müşteriyi silmek istediğinizden emin misiniz?
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseDeleteModal} color="primary">
      İptal
    </Button>
    <Button
      onClick={() => {
        handleDeleteCustomer(deleteCustomerId)
          .then(() => handleCloseDeleteModal())
          .catch(err => console.error("Silme hatası:", err));
      }}
      color="error"
      autoFocus
    >
      Sil
    </Button>
  </DialogActions>
</Dialog>

{/* Geri Yükleme Modalı */}
<Dialog
  open={isRestoreModalOpen}
  onClose={handleCloseRestoreModal}
  aria-labelledby="restore-dialog-title"
  aria-describedby="restore-dialog-description"
>
  <DialogTitle id="restore-dialog-title">
    {"Geri Yükleme İşlemi"}
  </DialogTitle>
  <DialogContent>
    <DialogContentText id="restore-dialog-description">
      Bu müşteriyi geri yüklemek istediğinizden emin misiniz?
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseRestoreModal} color="error">
      İptal
    </Button>
    <Button
      onClick={() => {
        handleRestoreCustomer(restoreCustomerId)
          .then(() => {
            handleCloseRestoreModal();
            toast.success("Müşteri başarıyla geri yüklendi");
          })
          .catch(err => {
            console.error("Geri yükleme hatası:", err);
            toast.error("Müşteri geri yüklenemedi");
          });
      }}
      color="primary"
      autoFocus
    >
      Geri Yükle
    </Button>
  </DialogActions>
</Dialog>
    </div>
  );
};

export default Customers;